/**
 * Adaptive poll loop for inbound message delivery.
 *
 * Polls GET /v1/sites/{siteId}/conversations/{conversationId}/messages?after=<id>
 * to deliver messages from merchant takeovers and to detect controller changes.
 *
 * Cadence:
 *   controller === 'ai'       → ~25s  (only needs to detect takeovers)
 *   controller === 'merchant' → ~3s   (merchant is actively typing)
 *
 * The loop pauses when:
 *   - document.hidden (resumes on visibilitychange)
 *   - No visitor interaction for IDLE_TIMEOUT_MS (resumes on focus/typing/send)
 *
 * The loop runs only while the chat panel is open.
 */

import type { WidgetApiClient, ControllerKind, ChatMessage, MessageEntry } from './types';

const POLL_AI_MS = 25_000;
const POLL_MERCHANT_MS = 3_000;
const IDLE_TIMEOUT_MS = 5 * 60 * 1_000; // 5 minutes

export interface PollerCallbacks {
  /** Called when new messages arrive from the server that the client hasn't seen. */
  onNewMessages: (messages: ChatMessage[]) => void;
  /** Called whenever the server reports a controller change. */
  onControllerChange: (controller: ControllerKind) => void;
  /** Returns the current set of message IDs already held by the client (for dedup). */
  getKnownMessageIds: () => ReadonlySet<string>;
  /** Returns the last known message id to use as the `after` cursor. */
  getLastMessageId: () => string | null;
  /** Returns the current controller as known to the client. */
  getController: () => ControllerKind;
}

export interface Poller {
  /** Start the poll loop. Safe to call multiple times — idempotent. */
  start: () => void;
  /** Stop the poll loop. */
  stop: () => void;
  /** Record that the visitor interacted; resets the idle timer. */
  recordInteraction: () => void;
}

export function createPoller(
  siteId: string,
  conversationId: string,
  token: string,
  apiClient: WidgetApiClient,
  callbacks: PollerCallbacks,
): Poller {
  let running = false;
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  let lastInteractionAt = Date.now();

  function clearScheduled(): void {
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle);
      timeoutHandle = null;
    }
  }

  function isIdle(): boolean {
    return Date.now() - lastInteractionAt > IDLE_TIMEOUT_MS;
  }

  async function tick(): Promise<void> {
    if (!running) return;
    if (document.hidden || isIdle()) {
      // Pause — will be resumed by event listeners
      return;
    }

    try {
      const lastId = callbacks.getLastMessageId();
      const response = await apiClient.pollMessages(siteId, token, conversationId, lastId);

      // Update controller state if it changed
      const currentController = callbacks.getController();
      if (response.controller !== currentController) {
        callbacks.onControllerChange(response.controller);
      }

      // Filter out messages the client already knows about (from SSE stream)
      if (response.messages.length > 0) {
        const knownIds = callbacks.getKnownMessageIds();
        const newMessages = response.messages
          .filter((m) => !knownIds.has(m.id))
          .map((m): ChatMessage => ({
            id: m.id,
            role: m.role,
            content: m.content,
            ts: new Date(m.created_at * 1000).toISOString(),
          }));
        if (newMessages.length > 0) {
          callbacks.onNewMessages(newMessages);
        }
      }
    } catch {
      // Network errors are transient — log nothing, just retry at next interval
    }

    if (!running) return;

    const interval = callbacks.getController() === 'merchant' ? POLL_MERCHANT_MS : POLL_AI_MS;
    timeoutHandle = setTimeout(() => { void tick(); }, interval);
  }

  function resume(): void {
    if (!running) return;
    clearScheduled();
    void tick();
  }

  const onVisibilityChange = (): void => {
    if (!document.hidden) resume();
  };

  const start = (): void => {
    if (running) return;
    running = true;
    lastInteractionAt = Date.now();
    document.addEventListener('visibilitychange', onVisibilityChange);
    void tick();
  };

  const stop = (): void => {
    running = false;
    clearScheduled();
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };

  const recordInteraction = (): void => {
    lastInteractionAt = Date.now();
    // If the loop was paused due to idle, kick it back into gear
    if (running && timeoutHandle === null && !document.hidden) {
      void tick();
    }
  };

  return { start, stop, recordInteraction };
}
